import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ChevronDown,
  Filter,
  KanbanSquare,
  LayoutList,
  Settings2,
} from "lucide-react";
import { handleExclusiveTableMenuToggle } from "./table-menu-events";

export type TableColumn = { key: string; label: string; required?: boolean };

// Os handlers DOM ficam em outro módulo para manter os exports compatíveis com Fast Refresh.
export function MultiSelectFilter({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ value: string; label: string }>;
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const toggle = (option: string) =>
    onChange(
      value.includes(option)
        ? value.filter((item) => item !== option)
        : [...value, option],
    );
  return (
    <details
      className="multi-filter"
      data-exclusive-table-menu
      onToggle={handleExclusiveTableMenuToggle}
    >
      <summary title={label}>
        <span>
          {value.length
            ? `${value.length} selecionado${value.length === 1 ? "" : "s"}`
            : label}
        </span>
        <ChevronDown />
      </summary>
      <div className="multi-filter-panel">
        <button type="button" onClick={() => onChange([])}>
          Limpar seleção
        </button>
        {options.map((option) => (
          <label key={option.value}>
            <input
              type="checkbox"
              checked={value.includes(option.value)}
              onChange={() => toggle(option.value)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </details>
  );
}

export function useTablePreferences(
  storageKey: string,
  columns: TableColumn[],
  initialView: "table" | "kanban" = "kanban",
) {
  const optional = useMemo(
    () =>
      columns.filter((column) => !column.required).map((column) => column.key),
    [columns],
  );
  const [hidden, setHidden] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(`${storageKey}:columns`) || "[]");
    } catch {
      return [];
    }
  });
  const [view, setView] = useState<"table" | "kanban">(() => {
    const saved = localStorage.getItem(`${storageKey}:view`);
    return saved === "table" || saved === "kanban" ? saved : initialView;
  });
  useEffect(
    () => localStorage.setItem(`${storageKey}:columns`, JSON.stringify(hidden)),
    [hidden, storageKey],
  );
  useEffect(
    () => localStorage.setItem(`${storageKey}:view`, view),
    [view, storageKey],
  );
  const toggle = (key: string) =>
    setHidden((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key],
    );
  const reset = () => setHidden([]);
  const className = [
    `table-view-${view}`,
    ...hidden.map((key) => `hide-column-${key}`),
  ].join(" ");
  return { hidden, view, setView, toggle, reset, className, optional };
}

export function TableDisplayControls({
  count,
  total,
  columns,
  hidden,
  view,
  onView,
  onToggle,
  onReset,
  showView = true,
  showViewLabels = false,
}: {
  count: number;
  total: number;
  columns: TableColumn[];
  hidden: string[];
  view: "table" | "kanban";
  onView: (value: "table" | "kanban") => void;
  onToggle: (key: string) => void;
  onReset: () => void;
  showView?: boolean;
  showViewLabels?: boolean;
}) {
  return (
    <div className="table-display-controls">
      <span className="table-count">
        <Filter />
        {count} de {total}
      </span>
      {showView && <div className="table-density-toggle" aria-label="Modo de visualização">
        <button
          type="button"
          className={view === "table" ? "active" : ""}
          aria-pressed={view === "table"}
          onClick={() => onView("table")}
          title="Tabela"
        >
          <LayoutList />
          {showViewLabels && <span>Tabela</span>}
        </button>
        <button
          type="button"
          className={view === "kanban" ? "active" : ""}
          aria-pressed={view === "kanban"}
          onClick={() => onView("kanban")}
          title="Kanban"
        >
          <KanbanSquare />
          {showViewLabels && <span>Kanban</span>}
        </button>
      </div>}
      <details
        className="column-menu"
        data-exclusive-table-menu
        onToggle={handleExclusiveTableMenuToggle}
      >
        <summary>
          <Settings2 />
          <span>Colunas</span>
          <ChevronDown />
        </summary>
        <div className="column-menu-panel">
          <strong>Visibilidade</strong>
          {columns.map((column) => (
            <label key={column.key}>
              <input
                type="checkbox"
                checked={!hidden.includes(column.key)}
                disabled={column.required}
                onChange={() => onToggle(column.key)}
              />
              <span>{column.label}</span>
            </label>
          ))}
          <button type="button" onClick={onReset}>
            Restaurar padrão
          </button>
        </div>
      </details>
    </div>
  );
}
